import { FC, useMemo, useState } from 'react';
import { isRecord } from '@repo/shared';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ChartExportButton,
  ChartExportHeader,
} from '@/components/charts/chart-export-button';
import {
  ChartExportFilter,
  FieldConfig,
  TabConfig,
} from '@/components/charts/types';
import { useChartExport } from '@/components/charts/use-chart-export';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { ChartConfig } from '@/components/ui/chart';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { cn } from '@/lib/utils';

interface BarChartProps {
  data: Array<Record<string, unknown>>;
  indexKey: string;
  title: string;
  tabs: Array<TabConfig>;
  fields?: Array<FieldConfig>;
  description?: string;
  height?: number;
  className?: string;
  yAxisTicks?: boolean;
  showTabTotals?: boolean;
  tooltipContent?: React.ComponentProps<typeof ChartTooltip>['content'];
  customControlElement?: React.ReactNode;
  exportFilters?: Array<ChartExportFilter>;
}

export const BarChart: FC<BarChartProps> = ({
  data,
  indexKey,
  title,
  description,
  tabs,
  fields,
  height = 250,
  className,
  yAxisTicks = false,
  showTabTotals = true,
  tooltipContent,
  customControlElement,
  exportFilters,
}) => {
  const [activeTab, setActiveTab] = useState<TabConfig | undefined>(tabs[0]);

  const activeTabLabel = activeTab?.label ?? '';
  const { captureRef, exportChart, isExporting } = useChartExport({
    title,
    activeTabLabel,
    exportFilters,
  });

  const hasFields = fields && fields.length > 0;

  // This functional calculates the totals for each tab, derived from all the potential tabs and fields.
  const tabTotals = useMemo(
    () =>
      tabs.reduce<Record<string, number>>((totals, tab) => {
        const total = data.reduce((sum, item) => {
          if (hasFields) {
            const categoryValue = item[tab.key];
            if (!isRecord<number>(categoryValue)) return sum;
            const subTotal = fields.reduce((fieldSum, field) => {
              const fieldValue = categoryValue[field.key];
              return fieldSum + (fieldValue ?? 0);
            }, 0);
            return sum + subTotal;
          }
          const value = item[tab.key];
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);

        return { ...totals, [tab.key]: total };
      }, {}),
    [data, tabs, fields, hasFields]
  );

  const chartConfig = useMemo((): ChartConfig => {
    if (hasFields) {
      return fields.reduce<ChartConfig>(
        (config, field) => ({
          ...config,
          [`${activeTab?.key}.${field.key}`]: {
            label: field.label,
            color: field.color,
          },
        }),
        {}
      );
    }

    return tabs.reduce<ChartConfig>(
      (config, tab, index) => ({
        ...config,
        [tab.key]: {
          label: tab.label,
          color: tab.color ?? `hsl(var(--chart-${(index % 5) + 1}))`,
        },
      }),
      {}
    );
  }, [tabs, fields, hasFields, activeTab?.key]);

  return (
    <Card className={cn('group gap-0 py-0', className)}>
      <CardHeader className='flex flex-col items-stretch border-b !p-0 sm:flex-row'>
        <div className='flex flex-1 flex-col justify-center gap-1 px-6 pt-4 pb-3 sm:!py-0'>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
          {customControlElement}
        </div>

        <div className='flex flex-wrap sm:flex-nowrap min-h-20'>
          {tabs.length > 1 &&
            tabs.map((tab) => (
              <button
                key={tab.key}
                data-active={activeTab?.key === tab.key}
                className='data-[active=true]:bg-muted/50 relative z-30 flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l sm:border-t-0 sm:border-l sm:px-8 sm:py-6 hover:bg-muted/20 transition-colors'
                onClick={() => {
                  setActiveTab(tab);
                }}>
                <span
                  className={cn(
                    'text-muted-foreground text-xs',
                    !showTabTotals && 'text-lg font-bold text-black'
                  )}>
                  {tab.label}
                </span>
                {showTabTotals && (
                  <span className='text-lg leading-none font-bold sm:text-3xl'>
                    {(tabTotals[tab.key] ?? 0).toLocaleString()}
                  </span>
                )}
              </button>
            ))}
        </div>
      </CardHeader>

      <CardContent className='relative px-2 pt-10 pb-4 sm:px-6 sm:pt-10 sm:pb-6'>
        <ChartExportButton
          onClick={exportChart}
          isExporting={isExporting}
        />
        <div
          ref={captureRef}
          className='bg-background'>
          <div
            data-chart-export-header
            className='hidden'>
            <ChartExportHeader
              title={title}
              description={description}
              activeTabLabel={activeTabLabel}
              exportFilters={exportFilters}
            />
          </div>
          <ChartContainer
            config={chartConfig}
            className='aspect-auto w-full min-h-[50px]'
            style={{ height: `${height}px` }}>
            <RechartsBarChart
              accessibilityLayer
              data={data}
              margin={{ left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              <YAxis
                tickLine={yAxisTicks}
                axisLine={yAxisTicks}
                tickMargin={8}
              />
              <XAxis
                dataKey={indexKey}
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value: string) =>
                  new Date(value).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })
                }
              />

              <ChartTooltip
                content={
                  tooltipContent ?? (
                    <ChartTooltipContent
                      nameKey={!hasFields ? activeTab?.key : undefined}
                    />
                  )
                }
              />

              {hasFields && <ChartLegend content={<ChartLegendContent />} />}

              {hasFields ? (
                fields.map((field) => (
                  <Bar
                    key={field.key}
                    dataKey={`${activeTab?.key ?? ''}.${field.key}`}
                    name={field.label}
                    fill={field.color}
                    radius={[0, 0, 0, 0]}
                    stackId={field.stackId}
                  />
                ))
              ) : (
                <Bar
                  dataKey={activeTab?.key ?? ''}
                  fill={`var(--color-${activeTab?.key ?? ''})`}
                  radius={[0, 0, 0, 0]}
                />
              )}
            </RechartsBarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};
